import React, { Suspense, useEffect, useReducer, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { __ } from 'i18n/index';
import { isNetplayPageActive } from 'react/netplay/netplayPageState';
import { isPhoneWidth } from 'react/dock/phoneWidth';
import { closeDockChat } from 'react/redux/actions/dockChat';
import openDockChat from 'react/dock/chat/openDockChat';
import useDockChatChannels from 'react/dock/chat/useDockChatChannels';
import { dockChatBadge, dockChatHasConversations } from 'react/dock/chat/dockChatSelectors';

const DockChatSheet = React.lazy(() => import(/* webpackChunkName: "dock-chat-sheet" */ 'react/dock/chat/DockChatSheet'));

// The dock's chat (docs/plans/dock-chat-redesign.md): the player's rooms, DMs
// and match chat on every page but the netplay page, which has the Chat view.
// Mounted for every logged-in player by react/dock/MatchDock; the Chat tab
// shows once there is something to open. With the match bar up, the control
// sits in the bar's corner (DockChatButton) instead of floating.
//
// The sheet is a lazy chunk and opens only from the control, a mention toast's
// Reply or a profile card's Message (react/dock/chat/openDockChat). Phones get
// no sheet: the control is a link to the Chat view.

function chatViewHref() {
	return typeof siteUrl === 'string' ? `${siteUrl}/netplay/chat` : '/netplay/chat';
}

/** The Chat control: opens and closes the sheet, or links to the Chat view on a phone. */
export function DockChatButton({ className }) {
	const dispatch = useDispatch();
	const open = useSelector((state) => state.dockChat.open);
	const hasConversations = useSelector(dockChatHasConversations);
	const { mention, count } = useSelector(dockChatBadge, sameBadge);
	if(!hasConversations) {
		return null;
	}
	const toggle = (event) => {
		if(isPhoneWidth()) {
			return;
		}
		event.preventDefault();
		dispatch(open ? closeDockChat() : openDockChat());
	};
	const badge = badgeText({ mention, count });
	return (
		<a className={`dock-chat-button ${className}`} href={chatViewHref()} aria-expanded={open} onClick={toggle}>
			<span className='dock-chat-button__label'>{__('Chat')}</span>
			{badge && (
				<span className={'dock-chat-button__badge' + (mention ? ' dock-chat-button__badge--mention' : '')}>
					{badge}
				</span>
			)}
		</a>
	);
}

function badgeText({ mention, count }) {
	if(mention) {
		return '@';
	}
	return count > 0 ? String(count) : null;
}

const sameBadge = (a, b) => a.mention === b.mention && a.count === b.count;

// The header Chat link's status slots (react/layout/header/SiteHeader: one in
// the desktop nav, one in the phone switch) carry the tab's badge. On the
// netplay page react/matchmaking/unified/header/NetplayHeaderBridge fills them.
function HeaderChatStatus() {
	const [slots, setSlots] = useState([]);
	const badge = badgeText(useSelector(dockChatBadge, sameBadge));
	useEffect(() => {
		setSlots(Array.from(document.querySelectorAll('[data-netplay-status="chat"]')));
	}, []);
	if(!badge) {
		return null;
	}
	return (
		<React.Fragment>
			{slots.map((slot, index) => ReactDOM.createPortal(
				<span className='netplay-nav-status netplay-nav-status--live'>{badge}</span>,
				slot,
				`chat-${index}`,
			))}
		</React.Fragment>
	);
}

// Client navigation onto the netplay page puts its Chat view up: the sheet closes.
function useNetplayPage(dispatch) {
	const [, rerender] = useReducer((count) => count + 1, 0);
	useEffect(() => {
		const navigated = () => {
			if(isNetplayPageActive()) {
				dispatch(closeDockChat());
			}
			rerender();
		};
		window.addEventListener('smashladder:navigated', navigated);
		return () => window.removeEventListener('smashladder:navigated', navigated);
	}, [dispatch]);
	return isNetplayPageActive();
}

function useCloseOnEscape(open, dispatch) {
	useEffect(() => {
		if(!open) {
			return undefined;
		}
		const closeOnEscape = (event) => {
			if(event.key === 'Escape') {
				dispatch(closeDockChat());
			}
		};
		document.addEventListener('keydown', closeOnEscape);
		return () => document.removeEventListener('keydown', closeOnEscape);
	}, [open, dispatch]);
}

export default function DockChat({ barShowing }) {
	const dispatch = useDispatch();
	const open = useSelector((state) => state.dockChat.open);
	const hasRooms = useSelector((state) => !!(state.chat.myChatRooms && state.chat.myChatRooms.size));
	const onNetplay = useNetplayPage(dispatch);
	useDockChatChannels(hasRooms, onNetplay);
	useCloseOnEscape(open && !onNetplay, dispatch);

	if(onNetplay) {
		return null;
	}
	return (
		<React.Fragment>
			<HeaderChatStatus />
			{open && (
				<Suspense fallback={null}>
					<DockChatSheet barShowing={barShowing} />
				</Suspense>
			)}
			{!barShowing && <DockChatButton className='dock-chat-tab' />}
		</React.Fragment>
	);
}
